import { useCallback, useEffect, useState } from 'react'
import { api, dropNulls } from './api'
import type { AmendmentLog } from './types'

export type CreateAmendmentLogInput = {
  fieldId: string
  seasonYear: number
  appliedOn: string
  substance: string
  substanceType?: string | null
  supplierName?: string | null
  rateAmount?: number | null
  rateUnit?: string | null
  eahd?: string | null
  applicatorName?: string | null
  notes?: string | null
}

export type UpdateAmendmentLogInput = Partial<Omit<CreateAmendmentLogInput, 'fieldId' | 'seasonYear'>>

export function useAmendmentLogs(
  orgId: string | undefined,
  farmId: string | undefined,
  fieldId: string | undefined,
  seasonYear: number,
) {
  const [logs, setLogs] = useState<AmendmentLog[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const base = orgId && farmId ? `/orgs/${orgId}/farms/${farmId}/amendment-logs` : null

  const loadLogs = useCallback(async () => {
    if (!base || !fieldId) return
    setLoading(true)
    setError(null)
    try {
      const { data } = await api.getPage<AmendmentLog>(`${base}?fieldId=${fieldId}&seasonYear=${seasonYear}`)
      setLogs(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load amendment logs.')
    } finally {
      setLoading(false)
    }
  }, [base, fieldId, seasonYear])

  useEffect(() => {
    loadLogs()
  }, [loadLogs])

  async function addLog(input: CreateAmendmentLogInput) {
    if (!base) return
    const created = await api.post<AmendmentLog>(base, dropNulls(input))
    setLogs((prev) => [created, ...prev])
    return created
  }

  async function updateLog(id: string, patch: UpdateAmendmentLogInput) {
    if (!base) return
    const updated = await api.patch<AmendmentLog>(`${base}/${id}`, patch)
    setLogs((prev) => prev.map((l) => (l.id === id ? updated : l)))
    return updated
  }

  return { logs, loading, error, addLog, updateLog }
}
